import { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, MRT_Row, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import dayjs from 'dayjs';
import { Badge, Box, Button, Grid, IconButton, Link, ListItemIcon, Menu, MenuItem, MenuProps, Paper, Tooltip, Typography, alpha, styled } from '@mui/material';
import assets from "../../../assets";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CssBaseline from '@mui/material/CssBaseline';
import { SkuSessionStorageKey, SkuSessionStorageModel, SkuTableParameterStorageService } from "../../../services/sessionStorage";
import RefreshIcon from '@mui/icons-material/Refresh';
import colorConfigs from "../../../configs/colorConfigs";
import { Link as RouterLink } from 'react-router-dom';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import { tsXLXS } from 'ts-xlsx-export';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import { MRT_FilterFns } from 'material-react-table';
import { useMediaQuery } from '@mui/material';
import RareButton from "../buttons/RareButton";
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import {
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';

const StyledMenu = styled((props: any) => (
  <Menu data-testid="Menu-jv2v"
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

interface SkuTableProps {
  rows: any[];
  isLoading: boolean;
  sorting: any;
  columnVisibility: any;
  columnFilters: any;
  pagination: any;
  globalFilter: any;
  showGlobalFilter: any;
  setShowGlobalFilter: (value: any) => void;
  handleRemoveAllFilters: () => void;
  setGlobalFilter: (value: string) => void;
  setSorting: (sorting: any[]) => void;
  setColumnVisibility: (columnVisibility: { [key: string]: boolean }) => void;
  setColumnFilters: (filters: any[]) => void;
  setPagination: (pagination: { pageSize: number; pageIndex: number }) => void;
  setSku: (detail: any) => void;
  setPopup: (detail: any) => void;
  handleRefreshButton: () => void;
  openEditPage: (detail: any) => void;
  openCreatePage: () => void;
}

const SkusTable: React.FC<SkuTableProps> = ({
  rows,
  isLoading,
  sorting,
  columnVisibility,
  columnFilters,
  pagination,
  globalFilter,
  showGlobalFilter,
  setShowGlobalFilter,
  setGlobalFilter,
  handleRemoveAllFilters,
  setSorting,
  setColumnVisibility,
  setColumnFilters,
  setPagination,
  setSku,
  setPopup,
  handleRefreshButton,
  openEditPage,
  openCreatePage,
}) => {

  console.log("rows", rows)
  const isMobile = useMediaQuery('(max-width:768px)');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSaveFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const customStatusFilter = (row: any, columnId: any, filterValue: any) => {
    const normalize = (str: string) => str.toLowerCase().replace(/_/g, ' ').trim(); // Convert to lowercase, replace underscores with spaces, and trim
    const status = normalize(row.getValue(columnId)?.toString() || ""); // Normalize the status value

    // Normalize filterValue to handle both single strings and arrays
    const normalizedFilterValues = Array.isArray(filterValue)
      ? filterValue.map((value) => normalize(value))
      : [normalize(filterValue)];
    // Check if any of the normalized filter values are partially included in the status
    return normalizedFilterValues.some((value) => status.includes(value));
  };

  const filterFns = {
    ...MRT_FilterFns,
    customStatusFilter, // Add your custom status filter
  };


  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      Header: () => (
        <Box data-testid="Box-nwff" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          ID<br />&nbsp;
        </Box>
      ),
    },
    {
      accessorKey: 'code',
      header: "Code",
      Header: () => (
        <Box data-testid="Box-o568" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Code<br />&nbsp;
        </Box>
      ),
      Cell: ({ cell }) => {
        // Get the strips from the row
        const label = (cell.getValue() as any) || "";


        return (
          <Typography data-testid="Typography-1xox" sx={{ fontWeight: 'bold' }}>{label}</Typography>
        );
      },
    },
    {
      // Custom column to render the 10 strip boxes.
      accessorKey: 'strips',
      header: 'Strips',
      Header: () => (
        <Box data-testid="Box-9j1v" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Strips<br />&nbsp;
        </Box>
      ),
      Cell: ({ cell, row }) => {
        // Get the strips from the row
        const strips = (cell.getValue() as any[]) || [];
        // Initialize an array with 10 null values
        const boxArray = new Array(10).fill(null);

        // Assuming the strip.position is 1-indexed and within 1-10 range
        strips.forEach((strip: any) => {
          const posIndex = strip.position;
          if (posIndex >= 0 && posIndex < 10) {
            boxArray[posIndex] = strip.label;
          }
        });

        return (
          <Box data-testid={`Box-9mry-code-${row.original.code}`} display="flex" justifyContent="space-evenly">
            {boxArray.map((label, index) => (
              <Box data-testid={`Drug-container-${index + 1}-code-${row.original.code}`} key={index}>
                {row.index === 0 && (  // Render this box ONLY for the first row.
                  <Box data-testid={`Drug-index-Box-${index + 1}-code-${row.original.code}`} sx={{ fontSize: '0.7rem' }}>
                    {index + 1}
                  </Box>
                )}
                <Box data-testid={`Drug-Box-${index + 1}-code-${row.original.code}`}
                  key={index}
                  sx={{
                    border: '2px solid',
                    borderColor: label ? 'primary.main' : 'grey.400',
                    borderRadius: 1,
                    padding: 1,
                    width: 37,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 0.5,
                    mt: 0
                  }}
                >
                  {label && <Typography data-testid={`Drug-Typography-${index + 1}-code-${row.original.code}`} sx={{ fontWeight: 'bold' }} variant="caption">{label}</Typography>}
                </Box>
              </Box>
            ))}
          </Box >
        );
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      Header: () => (
        <Box data-testid="Box-ry7r" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Status<br />&nbsp;
        </Box>
      ),
      Cell: ({ cell }) => (
        <Box data-testid="Box-17ak" sx={{ display: "flex", justifyContent: "center" }}>
          {cell.getValue()
            ? <Typography data-testid="Typography-s8hb" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.active}`, borderRadius: 2, color: colorConfigs.buttons.active }}>Active</Typography>
            : <Typography data-testid="Typography-lrev" fontSize="smaller" sx={{ paddingX: 0.7, border: `1.5px solid ${colorConfigs.buttons.inactive}`, borderRadius: 2, color: colorConfigs.buttons.inactive }}>Inactive</Typography>
          }
        </Box>
      ),
      filterVariant: 'checkbox',
    },
  ];



  // Define the type for the keys for handling persistance of table settings in session storage
  type SkuSessionStorageKeyType = keyof SkuSessionStorageModel;

  // Generic handler function for persisting table parameters
  const handleParameterChange = <T,>(
    key: SkuSessionStorageKeyType,
    currentValue: T,
    setValueFunction: (value: T) => void,
    newValue: T | ((prevValue: T) => T)
  ) => {
    const updatedValue = typeof newValue === 'function' ? (newValue as (prevValue: T) => T)(currentValue) : newValue;
    SkuTableParameterStorageService.set(key, updatedValue);
    setValueFunction(updatedValue);
  };

  // Handlers for persisting table: sorting, pagination, visibility, filter, search, and search bar visibility
  const setSortingFunction = (newSortingValue: any) =>
    handleParameterChange(SkuSessionStorageKey.sorting, sorting, setSorting, newSortingValue);
  const setPaginationChangeFunction = (newPaginationValue: any) =>
    handleParameterChange(SkuSessionStorageKey.pagination, pagination, setPagination, newPaginationValue);
  const setFilterChangeFunction = (newFilterChangeValue: any) =>
    handleParameterChange(SkuSessionStorageKey.columnFilters, columnFilters, setColumnFilters, newFilterChangeValue);
  const setGlobalFilterChangeFunction = (newGlobalFilterChangeValue: string | ((prevValue: string) => string)) =>
    handleParameterChange(SkuSessionStorageKey.search, globalFilter, setGlobalFilter, newGlobalFilterChangeValue);
  const setShowGlobalFilterChangeFunction = (newShowGlobalFilterChangeValue: any) =>
    handleParameterChange(SkuSessionStorageKey.showSearch, showGlobalFilter, setShowGlobalFilter, newShowGlobalFilterChangeValue);


  return <MaterialReactTable
    columns={columns}
    data={rows}
    filterFns={filterFns}
    state={{
      isLoading,
      sorting,
      columnFilters,
      pagination,
      globalFilter,
      showGlobalFilter
    }}
    onSortingChange={setSortingFunction}
    onColumnFiltersChange={setFilterChangeFunction}
    onPaginationChange={setPaginationChangeFunction}
    onGlobalFilterChange={setGlobalFilterChangeFunction}
    onShowGlobalFilterChange={setShowGlobalFilterChangeFunction}
    defaultColumn={{
      minSize: 10,
      maxSize: 160,
      size: 10,
    }}
    enableColumnOrdering={true}
    positionActionsColumn="last"
    enableRowSelection
    selectAllMode="all"
    // enableRowActions
    initialState={{
      sorting,
      columnVisibility,
      columnFilters,
      pagination,
      globalFilter,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select'], right: ['mrt-row-actions'] },
    }}

    // TABLE STYLING IS IN THE THEME
    displayColumnDefOptions={{
      'mrt-row-actions': {
        header: "Actions", // Renames Actions Column
      },
      'mrt-row-select': {
        visibleInShowHideMenu: false, //hide the built-in row actions column from the show hide menu
      },
    }}

    muiTopToolbarProps={({ table }) => ({
      sx: (theme: any) => ({
        ...((theme.components?.MuiCustomToolbar?.styleOverrides?.root) || {}),
      }),
    })}

    muiTableHeadProps={{
      sx: (theme: any) => theme.components?.MuiTableHead?.styleOverrides?.root || {},
    }}

    muiTableHeadCellProps={({ column }) => ({
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableCell?.styleOverrides?.head || {}),
        ...(column.id === 'mrt-row-select' && {
          maxWidth: '50px',
          width: '50px',
        }),
      }),
    })}

    muiFilterTextFieldProps={{
      sx: (theme: any) => theme.components?.MuiFilterTextField?.styleOverrides?.root || {},
    }}


    muiTableBodyProps={{
      sx: (theme: any) => theme.components?.MuiTableBody?.styleOverrides?.root || {},
    }}

    muiPaginationProps={{
      rowsPerPageOptions: [250, 500, 1000],
      sx: {
        padding: 0,
        margin: 0,
        '& .MuiTablePagination-root': {
          padding: 0,
          margin: 0,
        },
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          minHeight: '0px',
        },
        '& .MuiTablePagination-selectLabel': {
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          margin: 0,
        },
      },
    }}

    muiBottomToolbarProps={{
      // BOTTOM TOOLBAR (Pagination)
      sx: {
        height: '41px',
        maxHeight: '41px',
        minHeight: '41px',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
        margin: 0,
        padding: 0,
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          paddingBottom: 0,
          paddingTop: 0,
        },
        '& .MuiBox-root': {
          p: 0,
        },
        '& .MuiIconButton-root': {
          color: colorConfigs.tables.headBg,
        },
        '& .MuiTablePagination-root': {
          margin: 0,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          color: colorConfigs.buttons.bg,
        },
        '& .MuiTablePagination-selectLabel': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          color: colorConfigs.buttons.bg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
      },
    }}

    muiSelectAllCheckboxProps={{
      'data-testid': 'Select-all-checkbox',
    } as any}

    muiSelectCheckboxProps={({ row }) => ({
      'data-testId': `Select-row-with-code-${row.original.code}`,
    } as any)}

    muiTableBodyRowProps={({ row }) => ({
      // slap the test‑id on here
      'data-testid': `Row-with-code-${row.original.code}`,

      // your existing handlers and styles
      onClick: (event: any) => {
        console.log(row.original);
        openEditPage(row);
      },
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableBodyRow?.styleOverrides?.root || {}),
        backgroundColor:
          row.index % 2 === 0
            ? colorConfigs.tables.firstRow
            : colorConfigs.tables.secondRow,
      }),
    } as any)}

    muiTableBodyCellProps={({ cell, row }) => ({
      // this creates: e.g. id="email-cell-for-row-42"
      'data-testid': `${cell.column.id}-cell-for-row-with-code-${row.original.code}`,

      // preserve any other cell styles
      sx: {},
    } as any)}


    renderToolbarInternalActions={({ table }) => (
      <Box data-testid="Box-1u8y" sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
        {/* 1) Show/Hide Search */}
        <MRT_ToggleGlobalFilterButton
          table={table}
          data-testId={"Toggle-Search" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 2) Show/Hide Filters */}
        <MRT_ToggleFiltersButton
          table={table}
          data-testId={"Toggle-Filters" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 3) Show/Hide Columns */}
        <MRT_ShowHideColumnsButton
          table={table}
          data-testId={"Toggle-Columns" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}
        />

        {/* 4) Show/Hide Density */}
        <MRT_ToggleDensePaddingButton
          table={table}
          data-testId={"Toggle-Density" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 5) Toggle Full Screen */}
        <MRT_ToggleFullScreenButton
          table={table}
          data-testId={"Toggle-Fullscreen" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />
      </Box>
    )}

    renderTopToolbarCustomActions={({ table }) => {

      const handleArchiveCycles = () => {
        table.getSelectedRowModel().flatRows.map(row => {
          //Add archive logic
        });
      };

      const handleExportSkuRows = (rows: any[]) => {
        const excelRows: any[] = [];
        const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
        const filename = `${now}_SkuList`;

        rows.forEach(({ original: sku }) => {
          const excelRow: Record<string, any> = {
            ID: sku.id,
            Code: sku.code,
            Active: sku.is_active ? "Yes" : "No",
          };

          const stripSlots = new Array(10).fill(null);

          (sku.strips || []).forEach((strip: any) => {
            const posIndex = strip.position;
            if (posIndex >= 0 && posIndex < stripSlots.length) {
              stripSlots[posIndex] = strip;
            }
          });

          stripSlots.forEach((strip, i) => {
            excelRow[`Strip ${i + 1} Label`] = strip?.label ?? "";
            excelRow[`Strip ${i + 1} Threshold`] = strip?.threshold ?? "";
            excelRow[`Strip ${i + 1} Invalid Threshold`] = strip?.invalid_threshold ?? "";
            excelRow[`Strip ${i + 1} Test Codes`] =
              strip?.test_codes && strip.test_codes.length
                ? `[${strip.test_codes.join(', ')}]`
                : "";
          });

          excelRows.push(excelRow);
        });

        // fire off the export
        tsXLXS().exportAsExcelFile(excelRows).saveAsExcelFile(filename);
      };




      return (

        <Box data-testid="Box-ckqh" sx={{ display: 'flex', gap: '0.3rem', height: "40px", alignItems: 'center' }}>
          <Tooltip data-testid="Tooltip-cu4b" title="View action on selected data" placement="top" arrow>
            <Box data-testid="Box-bzau">
              <RareButton data-testid="RareButton-snkd"
                variant="outlined"
                onClick={handleClick}
                icon={<KeyboardArrowDownIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2, fontSize: '14px' }}
              >
                {isMobile ? "" : "Bulk Actions"}
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-k6ey" title="Refresh Data" placement="top" arrow>
            <Box data-testid="Box-tvb0">
              <RareButton data-testid="RareButton-pa8u"
                variant="outlined"
                onClick={() => handleRefreshButton()}
                center={true}
                icon={<RefreshIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-qssh" title="Clear All Table Filters" placement="top" arrow>
            <Box data-testid="Box-x763">
              <RareButton data-testid="RareButton-xwk1"
                variant="outlined"
                onClick={() => handleRemoveAllFilters()}
                icon={<FilterAltOffOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-2ba4" title="Create New Sku" placement="top" arrow>
            <Box data-testid="Box-c4d6">
              <RareButton data-testid="RareButton-f72a"
                onClick={openCreatePage}
                variant="success"
                icon={<BallotOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2 }}
              >
                {isMobile ? "" : "Create SKU"}
              </RareButton>
            </Box>
          </Tooltip>
          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              'aria-labelledby': 'demo-customized-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem data-testid="MenuItem-ee4s" disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            } onClick={() => handleExportSkuRows(table.getSelectedRowModel().rows)} disableRipple>
              Export to Excel
            </MenuItem>
          </StyledMenu>
        </Box>
      );
    }}

    renderRowActions={({ row, table }) => (

      <Box data-testid="Box-bc6c" sx={{ gap: '0.5rem', display: "flex" }}>
        {/* <Tooltip data-testid="Tooltip-sp0k"     
                    arrow
                    placement="left"
                    title="Cycles"
                    > */}
        <Badge data-testid="Badge-xblw"
          badgeContent={row.original.total_cycles}
          color={row.original.total_cycles ? 'primary' : 'default'}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              top: '13px',
              right: '10px',
              background: colorConfigs.tables.headBg,
            },
          }}
          overlap="circular"
        >
          <IconButton data-testid="IconButton-tqdl"
            disabled={!row.original.total_cycles}
            component={RouterLink}
            to={`/cycles?user_id=${row.original.id}`}
            state={{ clinicID: row.original.id }}
            sx={{ transition: 'transform 0.3s ease' }}
          >
            <RotateRightOutlinedIcon fontSize="large" sx={{ color: row.original.total_cycles ? colorConfigs.sidebar.color : colorConfigs.sidebar.bg }} />
          </IconButton>
        </Badge>
        {/* </Tooltip> */}
      </Box>
    )}
  />
}

export default SkusTable;

